import { useCallback, useEffect, useRef, useState } from 'react';

// Hands-free conversation for Ask AI and the Chief of Staff: listen → send →
// speak the answer → listen again, until you stop it. Built on what the browser
// already gives us (the same SpeechRecognition the mic buttons use, plus
// speechSynthesis), so there's no service to run and nothing new to pay for.
//
// Two rules shape the whole thing:
//   1. Never listen while speaking. The mic hears the phone's own voice and the
//      loop starts answering itself.
//   2. Everything starts from a tap. iOS refuses both microphone and speech
//      synthesis unless the first one comes from a real user gesture.

export const voiceSupported = () => typeof window !== 'undefined'
  && !!(window.SpeechRecognition || window.webkitSpeechRecognition)
  && !!window.speechSynthesis;

// Replies come back as chat markdown. Spoken aloud, "**" and "·" and bare URLs
// are noise, and a bullet list runs together without the pauses a reader adds.
export function speakableText(raw) {
  return String(raw || '')
    .replace(/```[\s\S]*?```/g, ' code block ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')       // links → their words
    .replace(/https?:\/\/\S+/g, ' link ')
    .replace(/[*_`#>|]/g, '')
    .replace(/^\s*[-•·]\s*/gm, ', ')                  // bullets → a breath
    .replace(/\n{2,}/g, '. ')
    .replace(/\n/g, ', ')
    .replace(/\s{2,}/g, ' ')
    // Bullets and blank lines both add punctuation, so a list leaves ". , ," behind.
    // Collapse runs of it or the voice stumbles over every item.
    .replace(/\s*([.,])(\s*[.,])+/g, '$1')
    .replace(/\s+([.,])/g, '$1')
    .trim();
}

export function useVoiceMode({ onUtterance, enabled = true }) {
  const [active, setActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [heard, setHeard] = useState('');          // live transcript, so you can see it took

  const recRef = useRef(null);
  const activeRef = useRef(false);                  // callbacks fire outside React's render
  const speakingRef = useRef(false);
  const onUtteranceRef = useRef(onUtterance);
  useEffect(() => { onUtteranceRef.current = onUtterance; }, [onUtterance]);

  const stopListening = useCallback(() => {
    const r = recRef.current;
    recRef.current = null;
    if (r) { try { r.onend = null; r.onresult = null; r.onerror = null; r.stop(); } catch {} }
    setListening(false);
  }, []);

  const listen = useCallback(() => {
    if (!activeRef.current || speakingRef.current || recRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = false;          // one utterance per turn — a pause means "your go"
    rec.interimResults = true;
    rec.lang = 'en-US';
    let finalText = '';
    rec.onresult = e => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += chunk; else interim += chunk;
      }
      setHeard((finalText + ' ' + interim).trim());
    };
    rec.onerror = () => { recRef.current = null; setListening(false); };
    rec.onend = () => {
      recRef.current = null;
      setListening(false);
      const said = finalText.trim();
      if (said) { setHeard(''); onUtteranceRef.current?.(said); }
      // Nothing said and still on? They're thinking — keep the mic open.
      else if (activeRef.current && !speakingRef.current) setTimeout(() => listen(), 400);
    };
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch { recRef.current = null; }
  }, []);

  // Speak a reply, then hand the mic back. Called by the chat when a new answer
  // lands, so the loop follows the actual conversation rather than a timer.
  const speak = useCallback(text => {
    const say = speakableText(text);
    if (!activeRef.current || !say) return;
    stopListening();                                  // rule 1: never both at once
    speakingRef.current = true; setSpeaking(true);
    try { window.speechSynthesis.cancel(); } catch {}
    const u = new window.SpeechSynthesisUtterance(say);
    u.rate = 1.02; u.pitch = 1;
    const done = () => {
      speakingRef.current = false; setSpeaking(false);
      if (activeRef.current) setTimeout(() => listen(), 250);
    };
    u.onend = done;
    u.onerror = done;                                 // a failed utterance must not end the loop
    try { window.speechSynthesis.speak(u); } catch { done(); }
  }, [listen, stopListening]);

  const start = useCallback(() => {
    if (!enabled || !voiceSupported()) return;
    activeRef.current = true; setActive(true); setHeard('');
    // iOS only unlocks audio if something is spoken inside the tap itself.
    try {
      const hello = new window.SpeechSynthesisUtterance('Listening');
      hello.rate = 1.1;
      hello.onend = () => listen();
      hello.onerror = () => listen();
      window.speechSynthesis.speak(hello);
    } catch { listen(); }
  }, [enabled, listen]);

  const stop = useCallback(() => {
    activeRef.current = false; setActive(false);
    speakingRef.current = false; setSpeaking(false);
    setHeard('');
    stopListening();
    try { window.speechSynthesis.cancel(); } catch {}
  }, [stopListening]);

  // Leaving the page must not leave a microphone open or a voice talking.
  useEffect(() => () => {
    activeRef.current = false;
    try { recRef.current?.stop(); } catch {}
    try { window.speechSynthesis?.cancel(); } catch {}
  }, []);

  return { supported: voiceSupported(), active, listening, speaking, heard, start, stop, speak };
}
